import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const cashflowApiClient = axios.create({
  baseURL: config.api.clientFormsServiceUrl || config.api.cashflowServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// All cashflow admin routes are namespaced under /admin/cashflow on the
// unified Client Forms service.
const ADMIN = '/admin/cashflow';

cashflowApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(cashflowApiClient);

applyTokenRefreshInterceptor(cashflowApiClient);

const cashflowService = {
  fetchAllClients: async () => {
    const response = await cashflowApiClient.get(`${ADMIN}/clients`);
    return response.data;
  },

  createClient: async (clientData) => {
    const response = await cashflowApiClient.post(`${ADMIN}/clients`, clientData);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await cashflowApiClient.delete(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  resendLink: async (clientId) => {
    const response = await cashflowApiClient.post(`${ADMIN}/clients/${clientId}/resend`);
    return response.data;
  },

  getLink: async (clientId) => {
    const response = await cashflowApiClient.post(
      `${ADMIN}/clients/${clientId}/resend?send_notification=false`
    );
    return response.data;
  },

  getClientDetail: async (clientId) => {
    const response = await cashflowApiClient.get(`${ADMIN}/clients/${clientId}`);
    return response.data;
  },

  patchClient: async (clientId, fields) => {
    const response = await cashflowApiClient.patch(`${ADMIN}/clients/${clientId}`, fields);
    return response.data;
  },

  scheduleSend: async (clientId, payload) => {
    const response = await cashflowApiClient.post(
      `${ADMIN}/clients/${clientId}/schedule-send`,
      payload,
    );
    return response.data;
  },

  cancelScheduledSend: async (clientId) => {
    const response = await cashflowApiClient.delete(
      `${ADMIN}/clients/${clientId}/schedule-send`,
    );
    return response.data;
  },

  exportClientExcel: async (clientId) => {
    const response = await cashflowApiClient.get(
      `${ADMIN}/clients/${clientId}/export`,
      { responseType: 'blob' }
    );
    return response;
  },

  /**
   * Persist (or update) the knowledgebase client association for a cashflow client.
   * Currently done by re-using the client record stored with create. A future
   * PATCH endpoint could be added; for now this is a no-op placeholder kept for
   * forward-compatibility.
   */
  updateKbClient: async (_clientId, _kbClientId) => {
    // Intentional no-op — knowledgebase_client_id is set at create time and
    // surfaced through the list/detail endpoints.
    return { success: true };
  },
};

export default cashflowService;
